import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { ArrowLeft, Eye, Save, Upload, X } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { AdminUser, getCurrentAdmin, logout } from "./api";
import {
  KnowledgeArticle,
  KnowledgeArticleCategory,
  KnowledgeArticlePayload,
  getAdminKnowledgeArticle,
  listAdminKnowledgeArticleCategories,
  saveAdminKnowledgeArticle,
  uploadAdminKnowledgeArticleImage,
} from "@/services/knowledge";
import { AdminSwitchCard } from "./components/AdminChoiceControl";
import { AdminFormNotice } from "./components/AdminFormNotice";
import { AdminRichTextEditor } from "./components/AdminRichTextEditor";
import { withImageFallback } from "@/utils/images";

type FormState = {
  Slug: string;
  Title: string;
  ArticleCategoryId: string;
  Category: string;
  Summary: string;
  Content: string;
  CoverImageUrl: string;
  Author: string;
  ReadTimeMinutes: string;
  PublishedAt: string;
  MetaTitle: string;
  MetaDescription: string;
  IsFeatured: boolean;
  IsPublished: boolean;
  SortOrder: string;
};

const emptyForm: FormState = {
  Slug: "",
  Title: "",
  ArticleCategoryId: "",
  Category: "",
  Summary: "",
  Content: "",
  CoverImageUrl: "",
  Author: "富聆聽專業聽力師",
  ReadTimeMinutes: "5",
  PublishedAt: new Date().toISOString().slice(0, 10),
  MetaTitle: "",
  MetaDescription: "",
  IsFeatured: false,
  IsPublished: true,
  SortOrder: "100",
};

function RequiredMark() {
  return <span className="ml-1 text-[#c3423f]" aria-hidden="true">*</span>;
}

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\p{L}\p{N}-]+/gu, "")
    .replace(/^-+|-+$/g, "");
}

function articleToForm(article: KnowledgeArticle): FormState {
  return {
    Slug: article.slug,
    Title: article.title,
    ArticleCategoryId: article.articleCategoryId ? String(article.articleCategoryId) : "",
    Category: article.category,
    Summary: article.summary,
    Content: article.content,
    CoverImageUrl: article.image,
    Author: article.author,
    ReadTimeMinutes: String(article.readTime || 5),
    PublishedAt: article.date || "",
    MetaTitle: article.metaTitle || "",
    MetaDescription: article.metaDescription || "",
    IsFeatured: article.isFeatured,
    IsPublished: article.isPublished,
    SortOrder: String(article.sortOrder || 100),
  };
}

function ArticlePreviewOverlay({ form, onClose }: { form: FormState; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[80] overflow-auto bg-white">
      <div className="sticky top-0 z-10 border-b border-[#dfe6e1] bg-white/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#9a8a7a]">文章預覽</div>
            <h2 className="text-xl font-bold text-[#1B3D2A]">{form.Title || "未命名文章"}</h2>
          </div>
          <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" type="button" onClick={onClose}>
            <X size={18} />
            關閉預覽
          </button>
        </div>
      </div>

      <main className="bg-white">
        <div className="bg-brand-green py-12 text-white md:py-16">
          <div className="mx-auto max-w-4xl px-6 md:px-10 lg:px-16">
            <span className="rounded-full bg-white/20 px-3 py-1.5 text-base font-medium">{form.Category || "文章分類"}</span>
            <h1 className="mt-4 text-2xl font-bold leading-snug md:text-3xl">{form.Title || "文章標題"}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-lg text-white/80">
              <span className="flex items-center gap-1"><i className="ri-user-line" /> {form.Author || "作者"}</span>
              <span className="flex items-center gap-1"><i className="ri-time-line" /> {form.ReadTimeMinutes || 1} 分鐘閱讀</span>
              <span className="flex items-center gap-1"><i className="ri-calendar-line" /> {form.PublishedAt || "未設定日期"}</span>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-4xl px-6 py-10 md:px-10 md:py-16 lg:px-16">
          <div className="mb-8 h-52 overflow-hidden rounded-2xl md:h-80">
            <img className="h-full w-full object-cover object-top" src={withImageFallback(form.CoverImageUrl)} alt={form.Title} />
          </div>
          <div className="space-y-6">
            <p className="text-base leading-relaxed text-gray-700 md:text-lg">{form.Summary || "文章摘要"}</p>
            <div
              className="prose prose-lg max-w-none text-gray-700 prose-headings:text-[#1B3D2A] prose-a:text-[#1B3D2A] prose-img:rounded-xl prose-iframe:aspect-video prose-iframe:w-full prose-iframe:rounded-xl"
              dangerouslySetInnerHTML={{ __html: form.Content || "<p>文章內容預覽</p>" }}
            />
          </div>
        </div>
      </main>
    </div>
  );
}

export default function AdminKnowledgeArticleForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const articleId = id ? Number(id) : null;
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [categories, setCategories] = useState<KnowledgeArticleCategory[]>([]);
  const [loading, setLoading] = useState(Boolean(articleId));
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [message, setMessage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    async function init() {
      try {
        const me = await getCurrentAdmin();
        if (!alive) return;
        setCurrentUser(me.User);
        const categoryRows = await listAdminKnowledgeArticleCategories();
        if (alive) setCategories(categoryRows.filter((item) => item.isActive));
        if (articleId) {
          const article = await getAdminKnowledgeArticle(articleId);
          if (alive) setForm(articleToForm(article));
        }
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "文章資料載入失敗。");
        navigate("/admin-login", { replace: true });
      } finally {
        if (alive) setLoading(false);
      }
    }
    void init();
    return () => {
      alive = false;
    };
  }, [articleId, navigate]);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function setField<Key extends keyof FormState>(key: Key, value: FormState[Key]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function handleTitleChange(value: string) {
    setForm((current) => ({
      ...current,
      Title: value,
      Slug: current.Slug || slugify(value),
      MetaTitle: current.MetaTitle || value,
    }));
  }

  async function handleCoverUpload(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    setMessage("");
    try {
      const url = await uploadAdminKnowledgeArticleImage(file);
      setField("CoverImageUrl", url);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "封面圖片上傳失敗。");
    } finally {
      setUploadingCover(false);
      event.target.value = "";
    }
  }

  function validate() {
    const errors: string[] = [];
    if (!form.Title.trim()) errors.push("請輸入文章標題。");
    if (!form.Slug.trim()) errors.push("請輸入網址代稱。");
    if (!form.ArticleCategoryId.trim()) errors.push("請選擇文章分類。");
    if (!form.Summary.trim()) errors.push("請輸入文章摘要。");
    if (!form.CoverImageUrl.trim()) errors.push("請上傳封面圖片。");
    if (!form.Author.trim()) errors.push("請輸入作者。");
    if (!form.Content.replace(/<[^>]*>/g, "").trim()) errors.push("請輸入文章內容。");
    if (Number(form.ReadTimeMinutes) < 1) errors.push("閱讀時間至少 1 分鐘。");
    return errors;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const errors = validate();
    if (errors.length > 0) {
      setMessage(errors[0]);
      return;
    }
    setSaving(true);
    setMessage("");
    const payload: KnowledgeArticlePayload = {
      Slug: slugify(form.Slug),
      Title: form.Title.trim(),
      ArticleCategoryId: Number(form.ArticleCategoryId),
      Category: categories.find((item) => String(item.id) === form.ArticleCategoryId)?.name || form.Category.trim(),
      Summary: form.Summary.trim(),
      Content: form.Content.trim(),
      CoverImageUrl: form.CoverImageUrl.trim(),
      Author: form.Author.trim(),
      ReadTimeMinutes: Number(form.ReadTimeMinutes || 1),
      PublishedAt: form.PublishedAt ? `${form.PublishedAt} 00:00:00` : null,
      MetaTitle: form.MetaTitle.trim(),
      MetaDescription: form.MetaDescription.trim(),
      IsFeatured: form.IsFeatured,
      IsPublished: form.IsPublished,
      SortOrder: Number(form.SortOrder || 100),
    };

    try {
      const saved = await saveAdminKnowledgeArticle(articleId, payload);
      navigate(`/admin/knowledge-articles/${saved.id}/edit`, { replace: true });
      setMessage("文章已儲存。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "文章儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout sectionLabel="內容管理" title={articleId ? "編輯文章" : "新增文章"} user={currentUser} onLogout={handleLogout}>
      <form onSubmit={handleSubmit}>
        <div className="sticky top-[76px] z-10 mb-5 rounded-md border border-[#dfe6e1] bg-white/95 p-3 shadow-[0_10px_24px_rgba(32,61,50,.08)] backdrop-blur md:top-[110px]">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => navigate("/admin/knowledge-articles")} type="button">
              <ArrowLeft size={18} />
              回文章列表
            </button>
            <div className="grid grid-cols-2 gap-2 sm:flex">
              <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => setPreviewOpen((current) => !current)} type="button">
                <Eye size={18} />
                {previewOpen ? "關閉預覽" : "預覽"}
              </button>
              <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving || loading} type="submit">
                <Save size={18} />
                {saving ? "儲存中" : "儲存文章"}
              </button>
            </div>
          </div>
        </div>

        <AdminFormNotice message={message} />

        {loading ? (
          <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-10 text-center text-[#59665f]">載入中...</div>
        ) : (
          <div className="grid gap-5 2xl:grid-cols-[minmax(0,1fr)_400px]">
            <div className="space-y-5">
              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">基本資訊</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="grid gap-1 md:col-span-2">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="article-title">文章標題<RequiredMark /></label>
                    <input id="article-title" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Title} onChange={(event) => handleTitleChange(event.target.value)} />
                  </div>
                  <div className="grid gap-1">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="article-slug">網址代稱 Slug<RequiredMark /></label>
                    <input id="article-slug" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Slug} onChange={(event) => setField("Slug", slugify(event.target.value))} placeholder="hearing-care-guide" />
                  </div>
                  <div className="grid gap-1">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="article-category">文章分類<RequiredMark /></label>
                    <select
                      id="article-category"
                      className="h-11 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                      value={form.ArticleCategoryId}
                      onChange={(event) => {
                        const selected = categories.find((item) => String(item.id) === event.target.value);
                        setForm((current) => ({ ...current, ArticleCategoryId: event.target.value, Category: selected?.name || "" }));
                      }}
                    >
                      <option value="">請選擇分類</option>
                      {categories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </select>
                  </div>
                  <div className="grid gap-1">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="article-author">作者<RequiredMark /></label>
                    <input id="article-author" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Author} onChange={(event) => setField("Author", event.target.value)} />
                  </div>
                  <div className="grid gap-1">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="article-date">發布日期</label>
                    <input id="article-date" type="date" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.PublishedAt} onChange={(event) => setField("PublishedAt", event.target.value)} />
                  </div>
                  <div className="grid gap-1 md:col-span-2">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="article-summary">文章摘要<RequiredMark /></label>
                    <textarea id="article-summary" className="min-h-[96px] rounded-md border border-[#cfd8d2] px-3 py-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Summary} onChange={(event) => setField("Summary", event.target.value)} placeholder="顯示於文章列表與文章頁標頭，建議 80-160 字" />
                  </div>
                </div>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">文章內容<RequiredMark /></h2>
                <AdminRichTextEditor value={form.Content} onChange={(value) => setField("Content", value)} onUploadImage={uploadAdminKnowledgeArticleImage} imageAlt="文章內容圖片" />
              </section>

            </div>

            <aside className="space-y-5">
              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">封面圖片<RequiredMark /></h2>
                <div className="overflow-hidden rounded-md border border-[#edf0ee] bg-[#f6f7f5]">
                  {form.CoverImageUrl ? <img className="h-48 w-full object-cover" src={form.CoverImageUrl} alt={form.Title} /> : <div className="flex h-48 items-center justify-center text-sm text-[#7b877f]">尚未上傳封面</div>}
                </div>
                <label className="mt-3 inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]">
                  <Upload size={18} />
                  {uploadingCover ? "上傳中" : "上傳封面"}
                  <input className="hidden" type="file" accept="image/*" onChange={handleCoverUpload} />
                </label>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">發布設定</h2>
                <div className="grid gap-3">
                  <AdminSwitchCard checked={form.IsPublished} description="開啟後會顯示在官網聽力知識列表與文章內頁。" label="上架文章" onChange={(checked) => setField("IsPublished", checked)} />
                  <AdminSwitchCard checked={form.IsFeatured} description="會顯示於聽力知識列表下方的編輯精選區塊。" label="設為精選" onChange={(checked) => setField("IsFeatured", checked)} />
                  <div className="grid gap-1">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="article-read-time">預估閱讀時間（分鐘）<RequiredMark /></label>
                    <input id="article-read-time" type="number" min={1} className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.ReadTimeMinutes} onChange={(event) => setField("ReadTimeMinutes", event.target.value)} />
                    <p className="text-xs leading-5 text-[#7b877f]">顯示在官網文章標頭，例如「5 分鐘閱讀」，協助讀者判斷閱讀所需時間。</p>
                  </div>
                  <div className="grid gap-1">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="article-sort">排序</label>
                    <input id="article-sort" type="number" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.SortOrder} onChange={(event) => setField("SortOrder", event.target.value)} />
                  </div>
                </div>
              </section>

              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <h2 className="mb-4 text-lg font-bold text-[#203d32]">SEO</h2>
                <div className="grid gap-3">
                  <div className="grid gap-1">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="article-meta-title">Meta Title</label>
                    <input id="article-meta-title" className="h-11 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.MetaTitle} onChange={(event) => setField("MetaTitle", event.target.value)} />
                  </div>
                  <div className="grid gap-1">
                    <label className="text-sm font-bold text-[#59665f]" htmlFor="article-meta-description">Meta Description</label>
                    <textarea id="article-meta-description" className="min-h-[88px] rounded-md border border-[#cfd8d2] px-3 py-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.MetaDescription} onChange={(event) => setField("MetaDescription", event.target.value)} />
                  </div>
                </div>
              </section>
            </aside>
          </div>
        )}
      </form>
      {previewOpen && <ArticlePreviewOverlay form={form} onClose={() => setPreviewOpen(false)} />}
    </AdminLayout>
  );
}
